import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  useMediaQuery,
  useTheme,
  alpha,
  Box,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { Icon } from "@iconify/react";

import { Strings } from "@app/constants";
import { deleteTestimonial } from "../slice";

interface DeleteTestimonialPopupProps {
  open: boolean;
  onClose: () => void;
  testimonialId: number | string | null;
  testimonialName?: string;
}

const DeleteTestimonialPopup: React.FC<DeleteTestimonialPopupProps> = ({
  open,
  onClose,
  testimonialId,
  testimonialName,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const handleDelete = () => {
    if (testimonialId) {
      dispatch(deleteTestimonial({ id: testimonialId }));
      onClose();
    }
  };

  const fontFamily = Strings.fontLanguage.workSans;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle sx={{ mt: 2, textAlign: "center" }}>
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: "50%",
            backgroundColor: alpha(theme.palette.error.main, 0.1),
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            color: theme.palette.error.main,
          }}
        >
          <Icon icon="solar:trash-bin-trash-bold" width={28} height={28} />
        </Box>
      </DialogTitle>

      <DialogContent sx={{ textAlign: "center" }}>
        <Typography
          variant="h6"
          fontWeight="bold"
          gutterBottom
          sx={{ color: "#101828" }}
        >
          Delete Review
        </Typography>
        <Typography sx={{ color: "#475467", fontSize: "0.95rem" }}>
          Are you sure you want to delete this review
          {testimonialName ? ` by "${testimonialName}"` : ""}? This action cannot
          be undone.
        </Typography>
      </DialogContent>

      <DialogActions
        sx={{
          justifyContent: fullScreen ? "center" : "flex-end",
          mb: 2,
          flexDirection: fullScreen ? "column" : "row",
          gap: fullScreen ? 1.5 : 1,
          px: 3,
        }}
      >
        <Button
          variant="outlined"
          onClick={onClose}
          fullWidth={fullScreen}
          sx={{
            color: theme.palette.primary.main,
            borderColor: theme.palette.primary.main,
            textTransform: "none",
            fontWeight: "600",
            fontFamily: fontFamily,
            "&:hover": {
              borderColor: theme.palette.primary.dark,
              backgroundColor: alpha(theme.palette.primary.main, 0.04),
            },
          }}
        >
          {Strings.button.cancel}
        </Button>
        <Button
          variant="contained"
          onClick={handleDelete}
          fullWidth={fullScreen}
          sx={{
            borderRadius: 0.5,
            backgroundColor: theme.palette.primary.main,
            color: "white",
            textTransform: "none",
            fontFamily: fontFamily,
            fontWeight: "600",
            "&:hover": {
              backgroundColor: theme.palette.primary.dark,
            },
          }}
        >
          {Strings.button.delete}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export { DeleteTestimonialPopup };
